import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useWantlistData } from '../../../../hooks/useWantlistData';
import { deckNumericId } from '../../utils/deckIds';
import BridgeSection from './BridgeSection';
import './CommandBridge.css';

const formatEuro = value =>
  value.toLocaleString('de-DE', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }) + ' €';

/**
 * Sichtbarkeit + numerische Deck-ID der Wantlist-Zeile — einzige Quelle der Wahrheit.
 *
 * Gibt die numerische DB-ID zurück, falls das Deck eine Wantlist-Vorschau zeigen soll
 * (eigenes virtuelles Deck), oder null wenn nicht.
 */
export function resolveWantlistDeckId(deck, isOwnProfile) {
  // Nur für eigene virtuelle Decks
  if (!isOwnProfile || deck?.source !== 'virtual') return null;
  return deckNumericId(deck);
}

/**
 * Berechnet die Wantlist-Kennzahlen eines Decks (Goldwert, fehlende Karten,
 * Freunde-Treffer). Basics filtern, `missing`-Formel, Oracle-Dedup mit
 * `friends`-Vereinigung.
 * Als benannter Export separat vom Export-Modal wiederverwendbar.
 */
export function useWantlistSummary(deck, isOwnProfile) {
  const deckId = resolveWantlistDeckId(deck, isOwnProfile);

  const { data, isLoading } = useWantlistData(
    deckId ? [deckId] : null,
    null,
    false, // includeOwned=false — nur fehlende Karten
  );

  return useMemo(() => {
    if (!deckId || !data?.cards) {
      return { totalValue: 0, missingCount: 0, friendsCount: 0, loaded: false };
    }

    const missing = data.cards
      .filter(c => {
        const isBasic =
          (c.superTypes || []).includes('Basic') &&
          (c.types || []).includes('Land');
        return !isBasic;
      })
      .map(c => {
        const ownedRelevant = c.printLocked
          ? (c.ownedPrintQuantity || 0)
          : (c.totalOwnedQuantity || 0);
        const missingQty = Math.max(0, (c.quantity || 0) - ownedRelevant);
        return { ...c, missing: missingQty };
      })
      .filter(c => c.missing > 0);

    // Deduplizieren nach oracleId (eine Karte kann mehrfach im Deck sein);
    // Mengen aggregieren und Friends-Listen vereinigen.
    const byOracle = new Map();
    for (const c of missing) {
      if (!c.oracleId) continue;
      const existing = byOracle.get(c.oracleId);
      if (existing) {
        existing.missing = (existing.missing || 0) + (c.missing || 0);
        if ((c.price || 0) > (existing.price || 0)) {
          existing.price = c.price;
        }
        const friendNames = new Set(existing.friends.map(f => f.name));
        for (const f of c.friends || []) {
          if (!friendNames.has(f.name)) {
            existing.friends.push(f);
            friendNames.add(f.name);
          }
        }
      } else {
        byOracle.set(c.oracleId, {
          ...c,
          friends: [...(c.friends || [])],
        });
      }
    }
    const deduped = [...byOracle.values()];

    const totalValue = deduped.reduce(
      (s, c) => s + (c.price || 0) * (c.missing || 0),
      0,
    );
    const missingCount = deduped.reduce((s, c) => s + (c.missing || 0), 0);
    const friendsCount = deduped.filter(c => (c.friends || []).length > 0).length;

    return { totalValue, missingCount, friendsCount, loaded: !isLoading };
  }, [data, isLoading, deckId]);
}

/**
 * Wantlist-Zeile der Kommandobrücke — sprechende Zusammenfassung
 * „X € · N fehlen · M bei Freunden →", führt per Klick auf `/wantlist?deckId=…`.
 * Kein `Link` um die `BridgeSection`: deren Kopf ist ein `<button>`, ein
 * `<a>` darum wäre invalides Nesting. Navigation läuft stattdessen über
 * `onToggle` + `useNavigate` — der Pfeil in `value` trägt die Link-Semantik
 * optisch, `BridgeSection` rendert ohne `open`/`children` ohnehin als reiner
 * Knopf ohne Chevron.
 */
export default function WantlistRow({ deck, isOwnProfile }) {
  const navigate = useNavigate();
  const summary = useWantlistSummary(deck, isOwnProfile);
  const wantlistDeckId = resolveWantlistDeckId(deck, isOwnProfile);

  if (!wantlistDeckId || !summary.loaded) return null;

  if (summary.missingCount === 0) {
    return (
      <BridgeSection label="Wantlist" value={<span className="bridge-wantlist__done">vollständig ✓</span>} />
    );
  }

  return (
    <BridgeSection
      label="Wantlist"
      onToggle={() => navigate(`/wantlist?deckId=${encodeURIComponent(deck.id)}`)}
      value={
        <>
          <span className="bridge-wantlist__gold">{formatEuro(summary.totalValue)}</span>
          {' · '}{summary.missingCount} fehlen
          {summary.friendsCount > 0 && <> · {summary.friendsCount} bei Freunden</>}
          {' '}<ArrowRight size={13} aria-hidden />
        </>
      }
    />
  );
}
